// convert to hook

import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { setLicenceValidity } from "../reducers/licence";
import { store } from "../ProviderApp";
import _ from "lodash";

// Standard API response wrapper type
export interface ApiResponse<T> {
  data: T;
  message?: string;
  success?: boolean;
}

// Branded type that causes a TS error when you try to use the response without specifying a type.
// e.g. axiosWrapper.post(url) → response.data is UnspecifiedResponseType → TS error on property access
type UnspecifiedResponseType = {
  readonly __brand: "ERROR: You must specify a response type — use axiosWrapper.post<YourResponseType>(...). Using <any> is not allowed.";
};

// When TResponse is `any`, this resolves to [never] — requiring an impossible extra argument.
// When TResponse is a real type, this resolves to [] — no extra argument needed.
// This makes axiosWrapper.post<any>(...) a compile error at the call site.
type NoAnyGuard<T> = 0 extends (1 & T) ? [never] : [];

// Typed wrapper functions - defaults to UnspecifiedResponseType to force callers to specify response types
// Specify response types at call sites: axiosWrapper.post<MyResponse>(...)
export const typedAxios = {
  post<TResponse = UnspecifiedResponseType, TRequest = unknown>(
    url: string,
    data?: TRequest,
    config?: AxiosRequestConfig,
    ...noAny: NoAnyGuard<TResponse>
  ): Promise<AxiosResponse<TResponse>> {
    return axios.post<TResponse>(url, data, config);
  },

  get<TResponse = UnspecifiedResponseType>(
    url: string,
    config?: AxiosRequestConfig,
    ...noAny: NoAnyGuard<TResponse>
  ): Promise<AxiosResponse<TResponse>> {
    return axios.get<TResponse>(url, config);
  },

  put<TResponse = UnspecifiedResponseType, TRequest = unknown>(
    url: string,
    data?: TRequest,
    config?: AxiosRequestConfig,
    ...noAny: NoAnyGuard<TResponse>
  ): Promise<AxiosResponse<TResponse>> {
    return axios.put<TResponse>(url, data, config);
  },

  delete<TResponse = UnspecifiedResponseType>(
    url: string,
    config?: AxiosRequestConfig,
    ...noAny: NoAnyGuard<TResponse>
  ): Promise<AxiosResponse<TResponse>> {
    return axios.delete<TResponse>(url, config);
  },

  patch<TResponse = UnspecifiedResponseType, TRequest = unknown>(
    url: string,
    data?: TRequest,
    config?: AxiosRequestConfig,
    ...noAny: NoAnyGuard<TResponse>
  ): Promise<AxiosResponse<TResponse>> {
    return axios.patch<TResponse>(url, data, config);
  },
};

axios.interceptors.request.use(
  (config) => {
    const returnedConfig = {
      ...config,
      onUploadProgress: (progressEvent: any) => {
        if (config.onUploadProgress) {
          (config.onUploadProgress as any)(
            Math.floor((progressEvent.loaded * 100) / progressEvent.total),
            progressEvent.loaded,
          );
        }
      },
    };
    returnedConfig.withCredentials = true;
    return returnedConfig;
  },
  (error) => Promise.reject(error),
);

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      _.get(error, "response.status") == "401" &&
      !["/googleLoginCallback", "/95789", "/signup", "/thank-you", "/pdfExport", "/"].includes(window.location.pathname)
    ) {
      localStorage.removeItem("isLogedIn");
      const errorMessage = error.response.data.errorMessage;

      const searchParams = new URLSearchParams();
      searchParams.set("redirectUrl", encodeURIComponent(window.location.pathname + window.location.search));
      if (errorMessage) {
        searchParams.set("errorMessage", encodeURIComponent(errorMessage));
      }

      const redirectUrl = `/?${searchParams.toString()}${window.location.hash}`;
      window.location.href = redirectUrl;
    } else if (_.get(error, "response.status") == "403" && _.get(error, "response.data.errorCode") === 1001) {
      console.log("licence expired");
      store.dispatch(setLicenceValidity(false));
    } else if (_.get(error, "response.status") == "429") {
      return false;
    }
    return Promise.reject(error);
  },
);

// Default export uses typed wrapper - call sites must specify response type or narrow the result
export default typedAxios;

// Named exports for flexibility
export const axiosWrapper = typedAxios;
